import {DateTime} from 'luxon';

import {addQueryToPath, parseQueryString} from '../../../libs/shared/helpers/src/browser/urlHelpers';
import {collectEnumValues} from '../../../libs/shared/helpers/src/typescript/enumHelpers';
import {isEnumType} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {AppRoutesPrefixesEnum} from '../../components/appRoutesPrefixes';
import {
  computeIsGlobalNamespace,
  globalNamespace,
  parseNamespaceForApplication,
} from '../../helpers/models/namespaceHelpers';
import {StartAndEndDatesWithPeriod} from '../../models/dates/dateIntervalModel';
import {
  AnalyticsLocation,
  AnalyticsParameters,
  AnalyticsParametersNamespacesEnum,
} from '../../store/panels/analytics/parameters/analyticsParameters';
import {AnalyticsSelectedViewId} from '../../store/panels/analytics/views/analyticsPanelViewsState';
import {AnalyticsReportsEnum} from './analyticsReportHelpers';

export interface AnalyticsUrlParams {
  namespace?: string;
  reportType?: AnalyticsReportsEnum;
  from?: DateTime;
  to?: DateTime;
  selectedViewId?: AnalyticsSelectedViewId;
}

const reportTypePathRegex = collectEnumValues(AnalyticsReportsEnum).join('|');

// An explantion of the namespace portion of this regex: when in the 'global' namespace we don't
// have id's - so the second half of the namespace url (where we define an id like tea:123) is optional.
// Also note that we don't want to capture the overall tea:123 value when it is present, just it's component
// parts (tea and 123) - so that is a non-capturing group, denoted by the '?:' prefix
const pathnameRegExp = new RegExp(
  `/${AppRoutesPrefixesEnum.ANALYTICS}/(teammate|team|global)(?:/(tea:|tim:)?([0-9]+))?/(${reportTypePathRegex})`,
);

export const parseInfoFromLocation = (location: AnalyticsLocation): AnalyticsUrlParams => {
  const {pathname, search} = location;

  // Parse pathname.
  const [, namespaceType, pathNamespacePrefix, namespaceId, reportType] =
    pathname.match(pathnameRegExp) || [];

  // Parse query params.
  const {from, to, selectedviewid} = parseQueryString(search); // parseQueryString returns a lowercased version of the query params
  return {
    namespace: computeNamespace(namespaceType, namespaceId, pathNamespacePrefix),
    reportType: isEnumType(reportType, AnalyticsReportsEnum) ? reportType : AnalyticsReportsEnum.OVERVIEW,
    from: DateTime.fromMillis(Number(from)) || undefined,
    to: DateTime.fromMillis(Number(to)) || undefined,
    selectedViewId: Number(selectedviewid) || selectedviewid, // At this point the view id can be "1" or "TMP_VIEW_1", in the first case we want to get the numeric value, otherwise we keep the string.
  };
};

export const buildReportPath = ({namespace, reportType, from, to, selectedViewId}: AnalyticsUrlParams) => {
  let path = `/${AppRoutesPrefixesEnum.ANALYTICS}`;
  if (namespace && computeIsGlobalNamespace(namespace)) {
    path += `/${globalNamespace}/${reportType}`;
  } else if (namespace) {
    const parsedNamespace = parseNamespaceForApplication(namespace);
    const namespacePrefix = computeNamespacePrefix(parsedNamespace.type);

    path += `/${parsedNamespace.type}/${namespacePrefix}:${parsedNamespace.id}/${reportType}`;
  }

  const query = {
    from: from?.toMillis(),
    to: to?.toMillis(),
    selectedViewId,
  };

  return addQueryToPath(path, query);
};

export const makeAnalyticsSelectedNamespaceUrl = (
  namespace: string,
  {reportType, query}: AnalyticsParameters,
): string =>
  buildReportPath({
    namespace,
    reportType,
    from: DateTime.fromMillis(query.from),
    to: DateTime.fromMillis(query.to),
  });

export const makeAnalyticsReportTypeUrl = (
  reportType: AnalyticsReportsEnum,
  parameters: AnalyticsParameters,
): string =>
  buildReportPath({
    namespace: getNamespaceFromParameters(parameters),
    reportType,
    from: DateTime.fromMillis(parameters.query.from),
    to: DateTime.fromMillis(parameters.query.to),
    selectedViewId: parameters.query.selectedViewId,
  });

export const getNamespaceFromParameters = (parameters: AnalyticsParameters): string => {
  if (parameters.namespace === AnalyticsParametersNamespacesEnum.TEAM_REPORT) {
    return parameters.teamId;
  }
  if (parameters.namespace === AnalyticsParametersNamespacesEnum.TEAMMATE_REPORT) {
    return parameters.teammateId;
  }
  return globalNamespace;
};

export const makeAnalyticsDateTimesUrl = (
  dates: StartAndEndDatesWithPeriod,
  parameters: AnalyticsParameters,
): string =>
  buildReportPath({
    namespace: getNamespaceFromParameters(parameters),
    reportType: parameters.reportType,
    from: dates.startDate,
    to: dates.endDate,
    selectedViewId: parameters.query.selectedViewId,
  });

export const makeAnalyticsSelectedViewUrl = (
  selectedViewId: AnalyticsSelectedViewId,
  parameters: AnalyticsParameters,
): string =>
  buildReportPath({
    namespace: getNamespaceFromParameters(parameters),
    reportType: parameters.reportType,
    from: DateTime.fromMillis(parameters.query.from),
    to: DateTime.fromMillis(parameters.query.to),
    selectedViewId,
  });

const computeNamespacePrefix = (namespaceKind: string): string => {
  if (namespaceKind === 'teammate') {
    return 'tea';
  }

  return 'tim';
};

const computeNamespace = (
  namespaceType: string,
  namespaceId: string,
  pathNamespacePrefix: string,
): string | undefined => {
  if (!namespaceType) {
    return undefined;
  }

  if (computeIsGlobalNamespace(namespaceType)) {
    return globalNamespace;
  }

  if (!namespaceId) {
    return undefined;
  }

  const namespacePrefix = pathNamespacePrefix ?? computeNamespacePrefix(namespaceType);
  return `${namespacePrefix}:${namespaceId}`;
};
